import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Ticket,
  RefreshCw,
  Plus,
  Copy,
  Check,
  Clock,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Package,
  CheckCircle,
  X,
  Link,
  Edit2,
  Save,
  Globe,
  Smartphone,
  AlertTriangle
} from 'lucide-react';
import './Certificates.css';

// Custom WhatsApp icon component
const WhatsAppIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Custom Telegram icon component
const TelegramIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

function Certificates() {
  const [certificates, setCertificates] = useState([]);
  const [stats, setStats] = useState({ total: 0, unused: 0, used: 0, expired: 0 });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [genCount, setGenCount] = useState(10);
  const [genMonths, setGenMonths] = useState(1);
  const [copiedCode, setCopiedCode] = useState(null);
  const [generatedCodes, setGeneratedCodes] = useState(null);
  const [waPhone, setWaPhone] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [ascConfig, setAscConfig] = useState(null);
  const [offerCodes, setOfferCodes] = useState(null);
  const [ascLoading, setAscLoading] = useState(false);
  const [syncingAppleId, setSyncingAppleId] = useState(null);
  const [syncingPending, setSyncingPending] = useState(false);

  const loadAscConfig = async () => {
    try {
      const res = await api.get('/api/admin/apple-offer-codes/config');
      setAscConfig(res.data.config || null);
    } catch (error) {
      console.error('ASC config error:', error);
    }
  };

  const discoverOfferCodes = async () => {
    setAscLoading(true);
    try {
      const res = await api.get('/api/admin/apple-offer-codes/discover');
      if (res.data.success) {
        setOfferCodes(res.data.offerCodes || []);
      }
    } catch (error) {
      console.error('Discover offer codes error:', error);
      alert(error.response?.data?.error || 'Не удалось загрузить кампании из App Store Connect');
    } finally {
      setAscLoading(false);
    }
  };

  const syncAppleCert = async (certId) => {
    setSyncingAppleId(certId);
    try {
      const res = await api.post(`/api/admin/apple-offer-codes/sync/${certId}`);
      if (!res.data.success && !res.data.result?.skipped) {
        alert(res.data.result?.error || res.data.error || 'Ошибка синхронизации с Apple');
      }
      loadCertificates();
      loadAscConfig();
    } catch (error) {
      alert(error.response?.data?.error || 'Ошибка синхронизации');
    } finally {
      setSyncingAppleId(null);
    }
  };

  const syncPendingApple = async () => {
    setSyncingPending(true);
    try {
      const res = await api.post('/api/admin/apple-offer-codes/sync-pending');
      if (res.data.failed > 0) {
        alert(`Синхронизировано: ${res.data.synced}, ошибок: ${res.data.failed}`);
      }
      loadCertificates();
    } catch (error) {
      alert(error.response?.data?.error || 'Ошибка массовой синхронизации');
    } finally {
      setSyncingPending(false);
    }
  };

  const getAppleSyncInfo = (cert) => {
    const status = cert.apple_sync_status || 'skipped';
    if (status === 'synced') return { label: 'Apple ✓', cls: 'apple-synced', hint: cert.apple_code };
    if (status === 'pending') return { label: 'Ожидание', cls: 'apple-pending', hint: cert.apple_code };
    if (status === 'failed') return { label: 'Ошибка', cls: 'apple-failed', hint: cert.apple_sync_error };
    if (status === 'skipped') return { label: '—', cls: 'apple-skipped', hint: 'ASC выключен или не настроен' };
    return { label: status, cls: 'apple-skipped', hint: '' };
  };

  const normalizeAppleCode = (code) => String(code || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();

  useEffect(() => {
    loadAll();
    loadAscConfig();
  }, []);

  useEffect(() => {
    loadCertificates();
  }, [page, filter]);

  const loadAll = () => {
    loadStats();
    loadCertificates();
  };

  const loadStats = async () => {
    try {
      const [allRes, unusedRes, usedRes, expiredRes] = await Promise.all([
        api.get('/api/admin/certificates?filter=all&limit=1').catch(() => ({ data: {} })),
        api.get('/api/admin/certificates?filter=unused&limit=1').catch(() => ({ data: {} })),
        api.get('/api/admin/certificates?filter=used&limit=1').catch(() => ({ data: {} })),
        api.get('/api/admin/certificates?filter=expired&limit=1').catch(() => ({ data: {} }))
      ]);
      setStats({
        total: allRes.data.pagination?.total || 0,
        unused: unusedRes.data.pagination?.total || 0,
        used: usedRes.data.pagination?.total || 0,
        expired: expiredRes.data.pagination?.total || 0
      });
      if (allRes.data.waPhone) setWaPhone(allRes.data.waPhone);
    } catch (error) {
      console.error('Stats error:', error);
    }
  };

  const loadCertificates = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/certificates?page=${page}&limit=20&filter=${filter}`);
      setCertificates(res.data.data || []);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
      if (res.data.waPhone) setWaPhone(res.data.waPhone);
    } catch (error) {
      console.error('Certificates error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const res = await api.post('/api/admin/certificates/generate', {
        count: genCount,
        months: genMonths
      });
      if (res.data.success) {
        if (res.data.waPhone) setWaPhone(res.data.waPhone);
        setGeneratedCodes({
          codes: res.data.certificates,
          months: genMonths,
          appleSync: res.data.appleSync,
        });
        if (res.data.appStoreConnect) setAscConfig(res.data.appStoreConnect);
        loadStats();
        loadCertificates();
      }
    } catch (error) {
      console.error('Generate error:', error);
      alert('Ошибка генерации сертификатов');
    } finally {
      setGenerating(false);
    }
  };

  const getWaLink = (code) => {
    // WhatsApp link with pre-filled message to send certificate to bot
    const waPhone = '37255509560'; // WhatsApp bot phone
    const message = `Отправить промо-код ${code} 👉`;
    return `https://wa.me/${waPhone}?text=${encodeURIComponent(message)}`;
  };

  const getWebUrl = (code) => {
    return `https://app.balletpost.com?cert=${code}&external=true`;
  };

  const getTgPromoUrl = (code) => {
    const baseUrl = window.location.hostname === 'localhost' 
      ? 'http://localhost:4000' 
      : 'https://app.balletpost.com';
    return `${baseUrl}?from=tg&cert=${code}&external=true`;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
  };

  const copyCode = async (code) => {
    await copyToClipboard(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const copyLink = async (code) => {
    const link = getWaLink(code);
    if (!link) return;
    await copyToClipboard(link);
    setCopiedCode('link_' + code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const copyWebUrl = async (code) => {
    const url = getWebUrl(code);
    await copyToClipboard(url);
    setCopiedCode('web_' + code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const copyTgPromoUrl = async (code) => {
    const url = getTgPromoUrl(code);
    await copyToClipboard(url);
    setCopiedCode('tg_' + code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const copyAllCodes = async () => {
    if (!generatedCodes) return;
    const text = generatedCodes.codes.join('\n');
    await copyToClipboard(text);
    setCopiedCode('__all__');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const copyAllLinks = async () => {
    if (!generatedCodes || !waPhone) return;
    const text = generatedCodes.codes.map(code => getWaLink(code)).join('\n');
    await copyToClipboard(text);
    setCopiedCode('__all_links__');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const copyAllWebUrls = async () => {
    if (!generatedCodes) return;
    const text = generatedCodes.codes.map(code => getWebUrl(code)).join('\n');
    await copyToClipboard(text);
    setCopiedCode('__all_web__');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const saveNote = async (certId, note) => {
    try {
      await api.post(`/api/admin/certificates/${certId}/note`, { note });
      setCertificates(prev => prev.map(c => c.id === certId ? { ...c, note } : c));
      setEditingNote(null);
    } catch (error) {
      console.error('Save note error:', error);
      alert('Ошибка сохранения');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const getStatusInfo = (cert) => {
    if (cert.is_used) return { label: 'Использован', cls: 'used' };
    if (new Date(cert.expires_at) <= new Date()) return { label: 'Истёк', cls: 'expired' };
    return { label: 'Свободен', cls: 'available' };
  };

  const getMonthsLabel = (raw) => {
    // postgres-sync-adapter coerces 0→false, 1→true — normalize first
    const m = raw === false ? 0 : raw === true ? 1 : Number(raw);
    if (m === 0) return '5 дней';
    if (m === 1) return '1 месяц';
    if (m >= 2 && m <= 4) return `${m} месяца`;
    return `${m} месяцев`;
  };

  const handleFilterChange = (f) => {
    setFilter(f);
    setPage(1);
  };

  if (loading && certificates.length === 0) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <span>Загрузка...</span>
      </div>
    );
  }

  return (
    <div className="certificates-page fade-in">
      <div className="page-header">
        <h1 className="page-title">Сертификаты</h1>
        <button className="btn btn-secondary" onClick={loadAll}>
          <RefreshCw size={18} />
          Обновить
        </button>
      </div>

      {/* Stats */}
      <div className="cert-stats-grid">
        <div className="stat-card" onClick={() => handleFilterChange('all')}>
          <div className="stat-icon cert-total">
            <Package size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Всего</span>
          </div>
        </div>
        <div className="stat-card" onClick={() => handleFilterChange('unused')}>
          <div className="stat-icon cert-available">
            <Ticket size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.unused}</span>
            <span className="stat-label">Свободных</span>
          </div>
        </div>
        <div className="stat-card" onClick={() => handleFilterChange('used')}>
          <div className="stat-icon cert-used">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.used}</span>
            <span className="stat-label">Использовано</span>
          </div>
        </div>
        <div className="stat-card" onClick={() => handleFilterChange('expired')}>
          <div className="stat-icon cert-expired">
            <XCircle size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.expired}</span>
            <span className="stat-label">Истекло</span>
          </div>
        </div>
      </div>

      {/* App Store Connect */}
      <div className={`asc-panel ${ascConfig?.enabled && ascConfig?.configured ? 'asc-ok' : 'asc-warn'}`}>
        <div className="asc-panel-header">
          <Smartphone size={20} />
          <h3>App Store Connect — единые промокоды для iPhone</h3>
        </div>
        <p className="asc-panel-desc">
          При генерации сертификат тот же код регистрируется в Apple (без дефисов).
          На iPhone пользователь вводит код в App Store; на сайте/Telegram — с дефисами.
        </p>
        {ascConfig ? (
          <div className="asc-panel-meta">
            <span>Статус: {ascConfig.enabled ? (ascConfig.configured ? '✅ включено' : '⚠️ нет ключей API') : '⏸ выключено (APP_STORE_CONNECT_ENABLED)'}</span>
            {ascConfig.subscriptionId && <span>Subscription ID: <code>{ascConfig.subscriptionId}</code></span>}
            {ascConfig.offerCodeMonths?.length > 0 && (
              <span>Кампании: {ascConfig.offerCodeMonths.map((m) => `${m}м→${ascConfig.offerCodeIds[m]?.slice(0, 8)}…`).join(', ')}</span>
            )}
          </div>
        ) : null}
        <div className="asc-panel-actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={loadAscConfig}>
            <RefreshCw size={16} /> Обновить статус
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={discoverOfferCodes} disabled={ascLoading}>
            {ascLoading ? 'Загрузка…' : 'Список кампаний ASC'}
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={syncPendingApple} disabled={syncingPending}>
            {syncingPending ? 'Синхронизация…' : 'Синхр. ошибки/pending'}
          </button>
        </div>
        {offerCodes && offerCodes.length > 0 && (
          <div className="asc-offer-list">
            <p className="asc-offer-list-title">Скопируйте ID в APP_STORE_CONNECT_OFFER_CODE_IDS:</p>
            <pre>{JSON.stringify(
              Object.fromEntries(offerCodes.map((o, i) => [String(i), o.id])),
              null,
              2
            )}</pre>
            <ul>
              {offerCodes.map((o) => (
                <li key={o.id}>
                  <code>{o.id}</code> — {o.name || o.offerCode || 'без имени'}
                  {o.active === false ? ' (неактивна)' : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
        {ascConfig?.enabled && !ascConfig?.offerCodeMonths?.length && (
          <p className="asc-panel-warn">
            <AlertTriangle size={14} /> Задайте APP_STORE_CONNECT_OFFER_CODE_IDS в .env (months → offer campaign id)
          </p>
        )}
      </div>

      {/* Generate Section */}
      <div className="cert-generate-section">
        <h3 className="section-title">
          <Plus size={20} />
          Генерация сертификатов
        </h3>
        <div className="generate-form">
          <div className="generate-field">
            <label className="input-label">Количество</label>
            <input
              type="number"
              className="input"
              value={genCount}
              onChange={(e) => setGenCount(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
              min="1"
              max="100"
            />
          </div>
          <div className="generate-field">
            <label className="input-label">Срок подписки</label>
            <select
              className="input"
              value={genMonths}
              onChange={(e) => setGenMonths(parseInt(e.target.value))}
            >
              <option value={0}>5 дней</option>
              <option value={1}>1 месяц</option>
              <option value={2}>2 месяца</option>
              <option value={3}>3 месяца</option>
              <option value={6}>6 месяцев</option>
              <option value={12}>12 месяцев</option>
            </select>
          </div>
          <button
            className="btn btn-primary generate-btn"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? (
              <>
                <div className="spinner-sm"></div>
                Генерация...
              </>
            ) : (
              <>
                <Plus size={18} />
                Сгенерировать {genCount} шт.
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="tabs-container">
        {[
          { key: 'all', label: 'Все', icon: Package },
          { key: 'unused', label: 'Свободные', icon: Ticket },
          { key: 'used', label: 'Использованные', icon: CheckCircle },
          { key: 'expired', label: 'Истёкшие', icon: XCircle }
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              className={`tab ${filter === tab.key ? 'active' : ''}`}
              onClick={() => handleFilterChange(tab.key)}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="cert-table-section">
        {certificates.length === 0 ? (
          <div className="empty-state">
            <Ticket size={48} />
            <h3>Нет сертификатов</h3>
            <p>Сгенерируйте первые сертификаты выше</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Код</th>
                    <th>Apple</th>
                    <th>Срок</th>
                    <th>Статус</th>
                    <th>Создан</th>
                    <th>Истекает</th>
                    <th>Использован</th>
                    <th>Примечание</th>
                  </tr>
                </thead>
                <tbody>
                  {certificates.map(cert => {
                    const status = getStatusInfo(cert);
                    return (
                      <tr key={cert.id}>
                        <td>
                          <div className="cert-code-cell">
                            <button
                              className="cert-code-btn"
                              onClick={() => copyCode(cert.code)}
                              title="Скопировать код"
                            >
                              <code className="cert-code">{cert.code}</code>
                              {copiedCode === cert.code ? (
                                <Check size={14} className="copy-icon copied" />
                              ) : (
                                <Copy size={14} className="copy-icon" />
                              )}
                            </button>
                            {!cert.is_used && (
                              <>
                                <button
                                  className="cert-link-btn cert-web-btn"
                                  onClick={() => copyWebUrl(cert.code)}
                                  title="Скопировать веб-ссылку"
                                >
                                  {copiedCode === 'web_' + cert.code ? (
                                    <Check size={13} className="copied" />
                                  ) : (
                                    <Globe size={13} />
                                  )}
                                </button>
                                {waPhone && (
                                  <button
                                    className="cert-link-btn cert-wa-btn"
                                    onClick={() => copyLink(cert.code)}
                                    title="Скопировать WhatsApp-ссылку"
                                  >
                                    {copiedCode === 'link_' + cert.code ? (
                                      <Check size={13} className="copied" />
                                    ) : (
                                      <WhatsAppIcon size={13} />
                                    )}
                                  </button>
                                )}
                                <button
                                  className="cert-link-btn cert-tg-btn"
                                  onClick={() => copyTgPromoUrl(cert.code)}
                                  title="Скопировать промо-ссылку для Telegram"
                                >
                                  {copiedCode === 'tg_' + cert.code ? (
                                    <Check size={13} className="copied" />
                                  ) : (
                                    <TelegramIcon size={13} />
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                          {!cert.is_used && cert.apple_code && (
                            <div className="apple-code-hint">iPhone: <code>{cert.apple_code}</code></div>
                          )}
                        </td>
                        <td>
                          {(() => {
                            const apple = getAppleSyncInfo(cert);
                            return (
                              <div className="apple-sync-cell">
                                <span className={`apple-badge ${apple.cls}`} title={apple.hint}>
                                  {apple.label}
                                </span>
                                {!cert.is_used && cert.apple_sync_status !== 'synced' && ascConfig?.enabled && (
                                  <button
                                    type="button"
                                    className="apple-retry-btn"
                                    title="Синхронизировать с Apple"
                                    disabled={syncingAppleId === cert.id}
                                    onClick={() => syncAppleCert(cert.id)}
                                  >
                                    <RefreshCw size={12} className={syncingAppleId === cert.id ? 'spin' : ''} />
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td>
                          <span className="months-badge">
                            {getMonthsLabel(cert.months)}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${status.cls}`}>
                            {status.label}
                          </span>
                        </td>
                        <td>{formatDate(cert.created_at)}</td>
                        <td>
                          <div className="date-cell">
                            <Clock size={14} />
                            {formatDate(cert.expires_at)}
                          </div>
                        </td>
                        <td>
                          {cert.is_used ? (
                            <div className="used-by-cell">
                              <span>{cert.username || cert.firstName || cert.used_by_user_id}</span>
                              <span className="used-date">{formatDate(cert.used_at)}</span>
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          {editingNote?.id === cert.id ? (
                            <div className="note-edit-cell">
                              <input
                                type="text"
                                className="input input-sm"
                                value={editingNote.note}
                                onChange={(e) => setEditingNote({ ...editingNote, note: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveNote(cert.id, editingNote.note);
                                  if (e.key === 'Escape') setEditingNote(null);
                                }}
                                autoFocus
                                placeholder="Кому выдан..."
                              />
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => saveNote(cert.id, editingNote.note)}
                                title="Сохранить"
                              >
                                <Save size={14} />
                              </button>
                            </div>
                          ) : (
                            <div className="note-cell" onClick={() => setEditingNote({ id: cert.id, note: cert.note || '' })}>
                              {cert.note ? (
                                <span className="note-text">{cert.note}</span>
                              ) : (
                                <span className="note-placeholder">
                                  <Edit2 size={12} />
                                  Добавить
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="cert-pagination">
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="pagination-info">
                  {page} / {pagination.pages}
                  <span className="pagination-total">({pagination.total} шт.)</span>
                </span>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={page >= pagination.pages}
                  onClick={() => setPage(page + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Generated Codes Modal */}
      {generatedCodes && (
        <div className="modal-overlay" onClick={() => setGeneratedCodes(null)}>
          <div className="modal cert-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                Сгенерировано {generatedCodes.codes.length} сертификатов
                ({getMonthsLabel(generatedCodes.months)})
              </h2>
              <button className="modal-close" onClick={() => setGeneratedCodes(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {generatedCodes.appleSync && (
                <p className="asc-gen-summary">
                  Apple: {generatedCodes.appleSync.synced} синхр.,
                  {generatedCodes.appleSync.failed > 0 && ` ${generatedCodes.appleSync.failed} ошибок,`}
                  {generatedCodes.appleSync.skipped > 0 && ` ${generatedCodes.appleSync.skipped} пропущено`}
                </p>
              )}
              <div className="generated-codes-list">
                {generatedCodes.codes.map((code, idx) => (
                  <div key={idx} className="generated-code-item">
                    <code className="cert-code">{code}</code>
                    <div className="generated-code-actions">
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => copyCode(code)}
                        title="Скопировать код"
                      >
                        {copiedCode === code ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                      <button
                        className="btn btn-ghost btn-sm cert-web-btn"
                        onClick={() => copyWebUrl(code)}
                        title="Скопировать веб-ссылку"
                      >
                        {copiedCode === 'web_' + code ? <Check size={14} /> : <Globe size={14} />}
                      </button>
                      {waPhone && (
                        <button
                          className="btn btn-ghost btn-sm cert-wa-link-btn"
                          onClick={() => copyLink(code)}
                          title="Скопировать WhatsApp-ссылку"
                        >
                          {copiedCode === 'link_' + code ? <Check size={14} /> : <Link size={14} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setGeneratedCodes(null)}>
                Закрыть
              </button>
              <button className="btn btn-secondary" onClick={copyAllWebUrls}>
                {copiedCode === '__all_web__' ? (
                  <>
                    <Check size={18} />
                    Скопировано!
                  </>
                ) : (
                  <>
                    <Globe size={18} />
                    Копировать веб-ссылки
                  </>
                )}
              </button>
              {waPhone && (
                <button className="btn btn-secondary" onClick={copyAllLinks}>
                  {copiedCode === '__all_links__' ? (
                    <>
                      <Check size={18} />
                      Скопировано!
                    </>
                  ) : (
                    <>
                      <Link size={18} />
                      Копировать WA-ссылки
                    </>
                  )}
                </button>
              )}
              <button className="btn btn-primary" onClick={copyAllCodes}>
                {copiedCode === '__all__' ? (
                  <>
                    <Check size={18} />
                    Скопировано!
                  </>
                ) : (
                  <>
                    <Copy size={18} />
                    Копировать коды
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Certificates;
